import { dontAwait } from "@glideapps/ts-necessities";
import React from "react";
import { useDebouncedValue } from "./use-debounced-value";

// !!! IMPORTANT !!!: THIS SHOULD NOT BE USED WITH ASYNC CALLS THAT MUTATE since the calls may not happen serially

// This hook does 2 things
// 1. It will debounce the argument to the async producer for the specifed `timeMS`
//    This means if many calls are made to the hook, the args used for the async producer are provided when `timeMS` is exceeded.
//    At this point, the producer is executed.
// 2. If for some reason another async request is made during this time, the pending requests will be "cancelled"
//    This avoids the following error scenario.
//    - the component calls the hook at time 0
//    - a debounced function with an async call (a1) is executed at time (100ms)
//    - another debounced function with an async call (a2) is executed at time (200ms)
//    - (a2) returns in 10ms
//    - (a1) returns in 100ms
//    - now we have stale data due to the async requests even though the debouncing worked as expected
// SOLUTION:
//    We "cancel" or more accurately, we ignore the result of any stale async requests. Stale is defined by any
//    pending async requests that have been initiated by prior debounced calls to the hook.
type AsyncProducer<Arg, Res> = (args: Arg) => Promise<Res>;
export function useConcurrentAsyncDebounced<Arg, Res>(
    producer: AsyncProducer<Arg, Res>,
    args: Arg,
    delayMS: number
): Res | null {
    const [response, setResponse] = React.useState<Res | null>(null);
    const debouncedValue = useDebouncedValue(args, delayMS);
    React.useEffect(() => {
        let canceled = false;
        const fn = async () => {
            const resp = await producer(debouncedValue);
            // if the result has been cancelled from another incoming request,
            // ignore setting state to avoid stale responses
            if (!canceled) {
                setResponse(resp);
            }
        };
        dontAwait(fn());
        return () => {
            canceled = true;
        };
    }, [debouncedValue, producer]);
    return response;
}
